import { toast } from "react-toastify";
import React, { useState } from "react";
import "react-toastify/dist/ReactToastify.css";
import CustomNotes from "./CustomNotes";
import {
  GraduationCap,
  BookOpen,
  Calendar,
  FileText,
  Search as SearchIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

// TODO: replace with the exact First Year Google Drive folder URL
const FIRST_YEAR_DRIVE_URL =
  "https://drive.google.com/drive/u/2/folders/1MebjEcVMMPX1aZn5mSPu5Xt2XuhxbYtw";
const subjects = {
  "First Year": {
    Foundation: {
      "semester 1": ["Common Resources"],
    },
  },

  "Second Year": {
    Computer: {
      "semester 1": [
        "Object Oriented programming and Computer Graphics",
        "Operating Systems",
        "Digital Electronics and Logic Design",
      ],
      //   "semester 2": ["M 3", "DSA", "SE", "MP", "PPL"],
    },
    IT: {
      "semester 1": [
        "Object Oriented Programming",
        "Basics of Computer Network",
        "Digital Electronics and Logic Design",
      ],
      // "semester 1": ["DM", "LDCO", "DSA", "OOP", "BCN"],
      // "semester 2": ["M3", "PA", "DBMS", "CG", "SE"],
    },
  },

  "Third Year": {
    Computer: {
      "semester 1": [
        "Database Management System",
        "Theory Of Computation",
        "Systems Programming and Operating System",
        "Computer Networks and Security",
        "Human Computer Interface",
      ],
      "semester 2": [
        "Data Science and Big Data Analytics",
        "Web Technology",
        "Artificial Intelligence",
        "Cloud Computing",
        "Information Security",
      ],
    },

    IT: {
      "semester 1": [
        "Theory of Computation",
        "Operating System",
        "Machine Learning",
        "Human Computer Interaction",
        "Design and Analysis of Algorithm",
        "Advance Database Management System",
      ],
      "semester 2": [
        "Data Science and Big Data Analytics",
        "Computer Network and Security",
        "Web Application Development",
        "Artificial Intelligence",
        "Cyber Security",
        "Cloud Computing",
      ],
    },
  },

  "Fourth Year": {
    Computer: {
      "semester 1": [
        "Design and Analysis of Algorithms",
        "Machine Learning",
        "Blockchain Technology",
        "Software Testing and Quality Assurance",
        "Cyber Security and Digital Forensics",
      ],
      "semester 2": [
        "High Performance Computing",
        "Deep Learning",
        "Natural Language Processing",
        "Image Processing",
        "Advanced Digital Signal Processing",
        "Soft Computing",
        "Quantum Computing",
      ],
    },

    IT: {
      "semester 1": [
        "Information Storage and Retrieval",
        "Software Project Management",
        "Deep Learning",
        "Wireless Communication",
        "Mobile Computing",
        "Introduction To Devops",
      ],
      "semester 2": [
        "Distributed Systems",
        "Natural Language Processing",
        "Soft Computing",
        "Business Analytics and Intelligence",
        "Blockchain Technology",
      ],
    },
  },
};

const SearchFunction = () => {
  const displayRef = React.useRef(null);
  const navigate = useNavigate();
  const { isLoggedIn, authChecked } = useAuth();
  const [year, setYear] = useState("");
  const [semester, setSemester] = useState("");
  const [type, setType] = useState("");
  const [subject, setSubject] = useState("");
  const [branch, setBranch] = useState("");
  const [branches, setBranches] = useState([]);
  const [notes, setNotes] = useState({
    year: "",
    branch: "",
    semester: "",
    subject: "",
    type: "",
  });

  const [, setSearchCount] = useState(0);
  const [shouldFetchLookup, setShouldFetchLookup] = useState(false);
  const [searchTrigger, setSearchTrigger] = useState(0); // New trigger to force re-fetch

  const isFirstYear = year === "First Year";

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === "year") {
      const yearBranches = Object.keys(subjects[value]);
      setBranches(yearBranches);
      setYear(value);
      setBranch("");
      setSemester("");
      setSubject(""); // Clear subject when year changes
      setNotes((prev) => ({
        ...prev,
        year: value,
        branch: "",
        semester: "",
        subject: "",
      }));
      setShouldFetchLookup(false);
    } else if (name === "branch") {
      setBranch(value);
      setSemester("");
      setSubject(""); // Clear subject when branch changes
      setNotes((prev) => ({
        ...prev,
        branch: value,
        semester: "",
        subject: "",
      }));
      setShouldFetchLookup(false);
    } else if (name === "semester") {
      setSemester(value);
      setSubject(""); // Clear subject when semester changes
      setNotes((prev) => ({ ...prev, semester: value, subject: "" }));
      setShouldFetchLookup(false);
    } else if (name === "type") {
      setType(value);
      setNotes((prev) => ({ ...prev, type: value }));
      setShouldFetchLookup(false);
    } else if (name === "subject") {
      setSubject(value);
      setNotes((prev) => ({ ...prev, subject: value }));
      setShouldFetchLookup(false);
    }
  };

  // Check if all fields are selected
  const getMissingFields = () => {
    if (isFirstYear) {
      return !notes.year || notes.year.trim() === "" ? ["Year"] : [];
    }
    const missing = [];
    if (!notes.year || notes.year.trim() === "") missing.push("Year");
    if (!notes.branch || notes.branch.trim() === "") missing.push("Branch");
    if (!notes.semester || notes.semester.trim() === "")
      missing.push("Semester");
    if (!notes.subject || notes.subject.trim() === "") missing.push("Subject");
    if (!notes.type || notes.type.trim() === "") missing.push("Material Type");
    return missing;
  };

  const handleSearch = () => {
    const missingFields = getMissingFields();
    if (missingFields.length > 0) {
      toast.warning(`Please select all fields: ${missingFields.join(", ")}`);
      return;
    }

    if (isFirstYear) {
      if (!authChecked) {
        toast.info("Checking login status, please try again.");
        return;
      }
      if (!isLoggedIn) {
        toast.error("Please log in to access First Year notes.");
        navigate("/login", { replace: true });
        return;
      }
      window.open(FIRST_YEAR_DRIVE_URL, "_blank", "noopener,noreferrer");
      return;
    }

    // console.log('🔎 Search button clicked with fields:', {
    //   year: notes.year,
    //   branch: notes.branch,
    //   semester: notes.semester,
    //   subject: notes.subject,
    //   type: notes.type
    // });

    setSearchCount((prev) => prev + 1);

    // Increment search trigger to force a new API call even if fields haven't changed
    setSearchTrigger((prev) => prev + 1);

    // Trigger the lookup API fetch
    setShouldFetchLookup(true);

    // Scroll to results section
    if (displayRef.current) {
      displayRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };
  const filteredSubjects =
    year && branch && semester
      ? subjects[year][branch][semester].filter(
          (sub) => sub && sub.trim() !== "",
        )
      : [];

  const isSearchDisabled = isFirstYear
    ? !notes.year
    : !notes.year ||
      !notes.branch ||
      !notes.semester ||
      !notes.subject ||
      !notes.type;

  return (
    <div className="space-y-8">
      {/* Header Card */}

      {/* Search Form Card */}
      <div className="rounded-xl border border-gray-800 bg-gray-900/40 backdrop-blur-sm p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-6">
          <div className="p-1.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
            <BookOpen className="w-4 h-4 text-indigo-400" />
          </div>
          <h2 className="text-xl font-semibold text-white">Course Selection</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Year Field */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
              <GraduationCap className="w-4 h-4 text-indigo-400" />
              Year
              {!notes.year && (
                <span className="text-xs text-red-400 font-normal">
                  (required)
                </span>
              )}
            </label>
            <select
              onChange={handleChange}
              name="year"
              value={year}
              className={`w-full text-white border ${!notes.year ? "border-red-500/40" : "border-gray-700"} bg-gray-800/50 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200 hover:bg-gray-800/70 hover:border-gray-600 cursor-pointer [&>option]:bg-gray-900 [&>option]:text-white [&>option]:py-2`}
              style={{ colorScheme: "dark" }}
            >
              <option value="" disabled hidden>
                Select Year
              </option>
              <option value="First Year">First Year</option>
              <option value="Second Year">Second Year</option>
              <option value="Third Year">Third Year</option>
              <option value="Fourth Year">Fourth Year</option>
            </select>
          </div>

          {/* Branch Field */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
              <GraduationCap className="w-4 h-4 text-indigo-400" />
              Branch
              {!notes.branch && (
                <span className="text-xs text-red-400 font-normal">
                  (required)
                </span>
              )}
            </label>
            <select
              onChange={handleChange}
              name="branch"
              value={branch}
              disabled={!notes.year || isFirstYear}
              className={`w-full text-white border ${!notes.branch ? "border-red-500/40" : "border-gray-700"} bg-gray-800/50 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200 hover:bg-gray-800/70 hover:border-gray-600 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed [&>option]:bg-gray-900 [&>option]:text-white [&>option]:py-2`}
              style={{ colorScheme: "dark" }}
            >
              <option value="" disabled hidden>
                {!notes.year
                  ? "Select Year First"
                  : isFirstYear
                    ? "Locked for First Year"
                    : "Select Branch"}
              </option>
              {branches.map((branch) => (
                <option key={branch} value={branch}>
                  {branch}
                </option>
              ))}
            </select>
          </div>

          {/* Semester Field */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
              <Calendar className="w-4 h-4 text-indigo-400" />
              Semester
              {!notes.semester && (
                <span className="text-xs text-red-400 font-normal">
                  (required)
                </span>
              )}
            </label>
            <select
              onChange={handleChange}
              name="semester"
              value={semester}
              disabled={!notes.year || !notes.branch || isFirstYear}
              className={`w-full text-white border ${!notes.semester ? "border-red-500/40" : "border-gray-700"} bg-gray-800/50 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200 hover:bg-gray-800/70 hover:border-gray-600 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed [&>option]:bg-gray-900 [&>option]:text-white [&>option]:py-2`}
              style={{ colorScheme: "dark" }}
            >
              <option value="" disabled hidden>
                {!notes.year
                  ? "Select Year First"
                  : !notes.branch
                    ? "Select Branch First"
                    : isFirstYear
                      ? "Locked for First Year"
                      : "Select Semester"}
              </option>
              <option value="semester 1">Semester 1</option>
              <option value="semester 2">Semester 2</option>
            </select>
          </div>

          {/* Subject Field */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
              <BookOpen className="w-4 h-4 text-indigo-400" />
              Subject
              {!notes.subject && (
                <span className="text-xs text-red-400 font-normal">
                  (required)
                </span>
              )}
            </label>
            <select
              onChange={handleChange}
              name="subject"
              value={subject}
              disabled={
                !notes.year || !notes.branch || !notes.semester || isFirstYear
              }
              className={`w-full text-white border ${!notes.subject ? "border-red-500/40" : "border-gray-700"} bg-gray-800/50 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200 hover:bg-gray-800/70 hover:border-gray-600 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed [&>option]:bg-gray-900 [&>option]:text-white [&>option]:py-2`}
              style={{ colorScheme: "dark" }}
            >
              <option value="" disabled hidden>
                {!notes.year
                  ? "Select Year First"
                  : !notes.branch
                    ? "Select Branch First"
                    : !notes.semester
                      ? "Select Semester First"
                      : isFirstYear
                        ? "Locked for First Year"
                        : "Select Subject"}
              </option>
              {filteredSubjects.map((subject) => (
                <option key={subject} value={subject}>
                  {subject}
                </option>
              ))}
            </select>
          </div>

          {/* Material Type Field */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
              <FileText className="w-4 h-4 text-indigo-400" />
              Material Type
              {!notes.type && (
                <span className="text-xs text-red-400 font-normal">
                  (required)
                </span>
              )}
            </label>
            <select
              onChange={handleChange}
              name="type"
              value={type}
              disabled={
                !notes.year ||
                !notes.branch ||
                !notes.semester ||
                !notes.subject ||
                isFirstYear
              }
              className={`w-full text-white border ${!notes.type ? "border-red-500/40" : "border-gray-700"} bg-gray-800/50 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200 hover:bg-gray-800/70 hover:border-gray-600 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed [&>option]:bg-gray-900 [&>option]:text-white [&>option]:py-2`}
              style={{ colorScheme: "dark" }}
            >
              <option value="" disabled hidden>
                {!notes.year
                  ? "Select Year First"
                  : !notes.branch
                    ? "Select Branch First"
                    : !notes.semester
                      ? "Select Semester First"
                      : !notes.subject
                        ? "Select Subject First"
                        : isFirstYear
                          ? "Locked for First Year"
                          : "Select Material Type"}
              </option>
              <option value="PYQ'S">PYQ'S</option>
              {/* <option value="Notes">Notes</option>               */}
            </select>
          </div>

          {/* Search Button - Full Width */}
          <div className="sm:col-span-2 lg:col-span-3">
            <button
              onClick={handleSearch}
              disabled={isSearchDisabled}
              className={`w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 ${
                isSearchDisabled
                  ? "bg-gray-700/50 cursor-not-allowed opacity-60 text-gray-400"
                  : "bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-400 hover:to-purple-500 text-white shadow-lg shadow-indigo-500/30 hover:shadow-xl hover:shadow-indigo-500/40"
              }`}
              title={
                getMissingFields().length > 0
                  ? `Please select: ${getMissingFields().join(", ")}`
                  : "Click to search for materials"
              }
            >
              <SearchIcon className="w-5 h-5" />
              {isSearchDisabled
                ? "Select All Fields to Search"
                : isFirstYear
                  ? "Open Google Drive"
                  : "Search Materials"}
            </button>
            {getMissingFields().length > 0 && (
              <p className="text-sm text-red-400 mt-2 text-center">
                Missing: {getMissingFields().join(", ")}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Results Section */}
      {!isFirstYear && (
        <div ref={displayRef}>
          <CustomNotes
            year={year}
            branch={branch}
            semester={semester}
            subject={subject}
            type={type}
            shouldFetch={shouldFetchLookup}
            searchTrigger={searchTrigger}
          />
        </div>
      )}
    </div>
  );
};

export default SearchFunction;
